import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import { NAME_MAX_LENGTH } from '@/lib/domain';
import { Icon } from './Icon';
import { IconButton } from './Button';
import { Kbd } from './Pill';
import s from './Field.module.css';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (id: string) => ReactNode;
  inline?: boolean;
  className?: string;
}

/** Label + control + hint/error, wired for accessibility. */
export function Field({ label, hint, error, children, inline, className }: FieldProps) {
  const id = useId();
  return (
    <div className={cx(s.field, inline && s.inline, className)}>
      {label && (
        <label htmlFor={id} className={s.label}>
          {label}
        </label>
      )}
      {children(id)}
      {error ? (
        <span className={s.error} role="alert">
          {error}
        </span>
      ) : (
        hint && <span className={s.hint}>{hint}</span>
      )}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { mono?: boolean; invalid?: boolean }>(
  function TextInput({ className, mono, invalid, ...rest }, ref) {
    return <input ref={ref} className={cx(s.input, mono && s.mono, invalid && s.invalid, className)} aria-invalid={invalid || undefined} {...rest} />;
  },
);

/** `used/50` under a name field; turns amber over the last five characters, like the phone app. */
export function NameCount({ value, className }: { value: string; className?: string }) {
  const used = value.length;
  return (
    <span className={cx(s.count, used >= NAME_MAX_LENGTH - 5 && s.countNearly, className)} aria-live="polite">
      {used}/{NAME_MAX_LENGTH}
    </span>
  );
}

/**
 * A name input capped at `NAME_MAX_LENGTH` (the server's `STRATEGY_NAME_MAX`) with a live count.
 * Enforced on the input, not validated on save, so the limit is met once, while typing.
 */
export const NameInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'maxLength'> & { value: string }>(
  function NameInput({ value, ...rest }, ref) {
    return (
      <>
        <TextInput ref={ref} value={value} maxLength={NAME_MAX_LENGTH} {...rest} />
        <NameCount value={value} />
      </>
    );
  },
);

/** A password field with a show/hide toggle. Starts hidden; the eye reveals what was typed. */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { invalid?: boolean }>(
  function PasswordInput({ className, invalid, ...rest }, ref) {
    const [shown, setShown] = useState(false);
    return (
      <div className={s.passwordWrap}>
        <input
          ref={ref}
          type={shown ? 'text' : 'password'}
          className={cx(s.input, s.passwordInput, invalid && s.invalid, className)}
          aria-invalid={invalid || undefined}
          {...rest}
        />
        <IconButton
          icon={shown ? 'visibility_off' : 'visibility'}
          label={shown ? 'Hide password' : 'Show password'}
          size="sm"
          className={s.passwordEye}
          onClick={() => setShown((v) => !v)}
          tabIndex={-1}
        />
      </div>
    );
  },
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={cx(s.input, s.textarea, className)} {...rest} />;
});

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export function Select<T extends string>({
  options,
  value,
  onChange,
  className,
  ...rest
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> & {
  options: ReadonlyArray<SelectOption<T>>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className={cx(s.selectWrap, className)}>
      <select className={cx(s.input, s.select)} value={value} onChange={(e) => onChange(e.target.value as T)} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="expand_more" size={18} className={s.selectIcon} />
    </div>
  );
}

export interface SearchFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  /** Shows the `/` hint; the page wires the shortcut with `useSlashFocus`. */
  shortcut?: boolean;
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { value, onChange, shortcut, className, placeholder = 'Search', ...rest },
  ref,
) {
  return (
    <label className={cx(s.search, className)}>
      <Icon name="search" size={18} />
      <input
        ref={ref}
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onChange('');
            (e.target as HTMLInputElement).blur();
          }
        }}
        {...rest}
      />
      {shortcut && !value && <Kbd>/</Kbd>}
    </label>
  );
});

/** On/off switch. */
export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={cx(s.toggle, checked && s.toggleOn)}
      onClick={() => onChange(!checked)}
    >
      <span className={s.knob} />
    </button>
  );
}

/** A range input with its value shown in a box beside it. */
export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  label,
  format = (v) => String(v),
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  label: string;
  format?: (v: number) => string;
}) {
  // Wide enough for the longest reading anywhere on the track ("0.75× ATR"), and fixed at that, so
  // the track never shortens mid-drag. The readout is monospace, so characters measure it exactly.
  let widest = 0;
  const steps = step > 0 ? Math.floor((max - min) / step) : 0;
  if (steps <= 500) for (let i = 0; i <= steps; i++) widest = Math.max(widest, format(min + i * step).length);
  return (
    <div className={s.slider}>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className={s.sliderValue} style={widest ? { width: `max(72px, calc(${widest}ch + 14px))` } : undefined}>
        {format(value)}
      </span>
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className={cx(s.checkbox, disabled && s.disabled)}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
